import { Injectable } from '@angular/core';

import StorageKeys from '../constants/storage';
import Constants from '../constants/constants';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  constructor() { }

  /**
  * Get the session ID
  * @param isNew boolean - whether to generate a new session ID or return the existing one 
  * @returns string - the session ID, which is a base64-encoded JSON string containing the expiry, key, email, and name (if available)
  */
  getSessionId(): string {
    const email = this.getItemFromSessionStorage(StorageKeys.CONTACT_DETAILS)?.email;
    let phoneNumber = this.getItemFromSessionStorage(StorageKeys.CONTACT_DETAILS)?.phoneNumber;
    const name = this.getItemFromSessionStorage(StorageKeys.CONTACT_DETAILS)?.firstName;

    // Remove +44 prefix if present in phone number
    if (phoneNumber && phoneNumber.includes('+44')) {
      phoneNumber = phoneNumber.replace('+44', '');
    }

    const emailOrPhone = email ? email : phoneNumber ? 'P' + phoneNumber + '@phone.com' : '';

    const STID = btoa(JSON.stringify({
      expiry: new Date(Date.now() + 3600 * 1000).toISOString(),
      key: "cognito-auth",
      ...(emailOrPhone ? { email: emailOrPhone } : {}),
      ...(name ? { name } : {})
    }));

    return STID;
  }


  /**
   * Sets an item into local storage.
   * @param key The key under which the value is stored.
   * @param value The value to store.
   * @returns void
   */
  setItemIntoLocalStorage(key: string, value: any): void {
    // Validate key and value before setting
    if (typeof value === 'undefined' || value === null || !value || !key) {
      return;
    }

    if (typeof value === 'string') {
      // Handle string values
      value = value.trim();

      if (value.length !== 0) {
        localStorage.setItem(key, value);
      }
    } else {
      // Handle non-string values by converting to JSON
      value = JSON.stringify(value);
      localStorage.setItem(key, value);
    }
  }

  /**
   * Gets an item from local storage.
   * @param key The key of the item to retrieve.
   * @returns The value stored under the key, or null if not found.
   */
  getItemFromLocalStorage(key: string): any {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Removes an item from local storage.
   * @param key The key of the item to remove.
   * @returns void
   */
  removeItemFromLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      return;
    }
  }

  /**
   * Clears all items from local storage.
   * @returns void
   */
  clearLocalStorage(): void {
    try {
      localStorage.clear();
    } catch (error) {
      return;
    }
  }

  /**
   * Sets an item into session storage.
   * @param key The key under which the value is stored.
   * @param value The value to store.
   * @returns void
   */
  setItemIntoSessionStorage(key: string, value: any): void {
    // Validate key and value before setting
    if (typeof value === 'undefined' || value === null || !value || !key) {
      return;
    }

    if (typeof value === 'string') {
      // Handle string values
      value = value.trim();

      if (value.length !== 0) {
        sessionStorage.setItem(key, value);
      }
    } else {
      // Handle non-string values by converting to JSON
      value = JSON.stringify(value);
      sessionStorage.setItem(key, value);
    }
  }

  /**
   * Gets an item from session storage.
   * @param key The key of the item to retrieve.
   * @returns The value stored under the key, or null if not found.
   */
  getItemFromSessionStorage(key: string): any {
    try {
      const item = sessionStorage.getItem(key);
      return (item) ? JSON.parse(item) : null;
    } catch (error) {
      return sessionStorage.getItem(key) || null; // Return as string if JSON parsing fails
    }
  }

  /**
   * Captures the accepted UTM params from the current URL into session storage.
   * Only keys listed in Constants.ALLOWED_UTM_PARAMS are kept, all other params are ignored.
   * @returns void
   */
  captureUtmParams(): void {
    const queryParams = new URLSearchParams(window.location.search);
    const utmParams: { [key: string]: string } = {};

    Constants.ALLOWED_UTM_PARAMS.forEach(key => {
      const value = queryParams.get(key)?.trim();
      if (value) {
        utmParams[key] = value;
      }
    });

    // Only overwrite stored params when the URL actually carries UTM params
    if (Object.keys(utmParams).length) {
      this.setItemIntoSessionStorage(StorageKeys.UTM_PARAMS, utmParams);
    }
  }

  /**
   * Gets the captured UTM params from session storage.
   * @returns The stored UTM params, or an empty object if none are available.
   */
  getUtmParams(): { [key: string]: string } {
    const utmParams = this.getItemFromSessionStorage(StorageKeys.UTM_PARAMS);
    return utmParams && typeof utmParams === 'object' ? utmParams : {};
  }

  /**
   * Removes an item from session storage.
   * @param key The key of the item to remove.
   * @returns void
   */
  removeItemFromSessionStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      return;
    }
  }

  /**
   * Clears all items from session storage.
   * @returns void
   */
  clearSessionStorage(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      return;
    }
  }
}
